import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Composable for infinite scroll using native Intersection Observer
 * @param {Function} callback - Function to call when reaching bottom
 * @param {Object} options - Options for the observer
 * @param {number} options.offset - Distance from bottom to trigger (default: 20px)
 * @param {number} options.threshold - Intersection threshold (default: 0.1)
 * @returns {Object} - { target: ref to attach to sentinel element }
 */
export function useInfiniteScroll(callback, options = {}) {
  const { offset = 20, threshold = 0.1 } = options;
  const target = ref(null);
  let observer = null;

  const handleIntersect = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback();
      }
    });
  };

  onMounted(() => {
    if (target.value) {
      observer = new IntersectionObserver(handleIntersect, {
        threshold,
        rootMargin: `${offset}px`
      });
      observer.observe(target.value);
    }
  });

  onUnmounted(() => {
    if (observer && target.value) {
      observer.unobserve(target.value);
      observer.disconnect();
    }
  });

  return {
    target
  };
}

/**
 * Directive for infinite scroll (Vue 2 style compatibility)
 * Usage: v-infinite-scroll="handleScroll"
 */
export const infiniteScrollDirective = {
  mounted(el, binding) {
    // Support both function and object value formats
    const callback = typeof binding.value === 'function' ? binding.value : binding.value?.callback;
    const offset = typeof binding.value === 'object' ? (binding.value.offset || 20) : 20;
    
    const handleScroll = () => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      
      // Check if scrolled to bottom (within offset px)
      if (scrollHeight - scrollTop - clientHeight < offset) {
        callback();
      }
    };
    
    el.addEventListener('scroll', handleScroll);
    el._infiniteScrollHandler = handleScroll;
  },
  
  unmounted(el) {
    if (el._infiniteScrollHandler) {
      el.removeEventListener('scroll', el._infiniteScrollHandler);
      delete el._infiniteScrollHandler;
    }
  }
};
